<script lang="ts">
	import type {
		AvtaleInfo,
		AvtaleFaq,
		AvtaleInteractiveModule,
		AvtaleCustomerReviews,
		AvtaleOtherDeals,
		AvtalePerks as AvtalePerksType,
		AvtalePersonBanner,
		AvtaleCustomerSupport,
		AvtaleDiscountSection,
		AvtaleCampaignBanner,
	} from "#lib/types/avtale.js";
	import type { InternalLinkingModule, SanityPicture } from "#lib/types/shared.js";
	import type {
		PageBanner,
		PageContent,
		PageCover,
		PageGallery,
		PageFaqs,
		PageCustomerServiceBanner,
	} from "#lib/types/page.js";
	import type { PageBuilderBlock } from "#lib/types/spotprisenContent.js";
	import { PortableText } from "@portabletext/svelte";
	import { contentBlockComponents } from "@ishavskraft/shared";

	import InternalLinking from "#lib/InternalLinking.svelte";
	import CustomerSupportBanner from "./CustomerSupportBanner.svelte";
	import InteractiveModule from "./InteractiveModule.svelte";
	import OtherDeals from "./OtherDeals.svelte";
	import AvtaleFAQ from "./AvtaleFAQ.svelte";
	import CustomerReviews from "./CustomerReviews.svelte";
	import AvtalePerks from "./AvtalePerks.svelte";
	import PersonBanner from "./PersonBanner.svelte";
	import DiscountSection from "./DiscountSection.svelte";
	// Landingsside-blokker, migrert fra den gamle inline-byggeren i [slug].
	import CoverBlock from "#lib/CoverPicture.svelte";
	import Banner from "#lib/Banner.svelte";
	import FAQ from "#lib/FAQ.svelte";
	import CustomerServiceBanner from "#lib/CustomerServiceBanner.svelte";
	import { SanityPicture as Picture } from "@ishavskraft/shared";
	import { breakpoints as br } from "#lib/utils/getBreakpoints.js";

	let { blocks }: { blocks: PageBuilderBlock[] } = $props();
</script>

{#each blocks as block (block._key)}
	{#if block._type === "info"}
		{@const avtaleInfo = block as AvtaleInfo}
		<div class="mx-edge-x mb-8 tablet:mb-11">
			{@render Info(avtaleInfo)}
		</div>
	{:else if block._type === "customerSupport"}
		{@const customerSupport = block as AvtaleCustomerSupport}
		<CustomerSupportBanner {customerSupport} />
	{:else if block._type === "interactiveModule"}
		{@const module = block as AvtaleInteractiveModule}
		<InteractiveModule {module} />
	{:else if block._type === "otherDeals"}
		{@const deals = block as AvtaleOtherDeals}
		<OtherDeals {deals} />
	{:else if block._type === "faqs"}
		{@const faqs = block as AvtaleFaq}
		<AvtaleFAQ {faqs} />
	{:else if block._type === "customerReviews"}
		{@const reviews = block as AvtaleCustomerReviews}
		<CustomerReviews {reviews} />
	{:else if block._type === "perks"}
		{@const perks = block as AvtalePerksType}
		<AvtalePerks {perks} />
	{:else if block._type === "internalLinkingModule"}
		<InternalLinking module={block.module as InternalLinkingModule} />
	{:else if block._type === "personBanner"}
		{@const personBanner = block as AvtalePersonBanner}
		<PersonBanner {personBanner} />
	{:else if block._type === "discountSection"}
		{@const discountSection = block as AvtaleDiscountSection}
		<DiscountSection {discountSection} />
	{:else if block._type === "campaignBanner"}
		{@const banner = block as AvtaleCampaignBanner}
		<div class="mx-edge-x pt-8">
			<div
				class="bg-[--reinlav-30] px-4 laptop:px-8 py-4 rounded-xl border-solid border-[--skog] border-1"
			>
				<p class="font-bold text-xl mb-2">{banner.title}</p>
				<PortableText value={banner.description?.filter(Boolean) ?? []} />
			</div>
		</div>
		<!-- Landingsside-blokker (migrert fra den gamle inline-byggeren) -->
	{:else if block._type === "content"}
		{@const content = block as PageContent}
		<div class="mx-edge-x laptop:mx-auto max-w-prose">
			<PortableText
				value={content.content}
				components={contentBlockComponents}
				onMissingComponent={false}
			/>
		</div>
	{:else if block._type === "cover"}
		{@const cover = block as PageCover}
		<CoverBlock image={cover.cover.image}>
			<h2 class="my-0 text-h-hero font-title tablet:leading-tight">
				{cover.cover.title}
			</h2>
		</CoverBlock>
	{:else if block._type === "banner"}
		<Banner block={block as PageBanner} />
	{:else if block._type === "pageFaqs"}
		{@const faq = block as PageFaqs}
		<section class="my-12 mx-edge-x">
			<h2>{faq.faqs.heading || `Spørsmål og svar`}</h2>
			<div class="border-solid border-3 rounded-2xl p-2">
				{#each faq.faqs.items as question (question)}
					<FAQ answer={question} />
				{/each}
			</div>
		</section>
	{:else if block._type === "customerServiceBanner"}
		{@const csb = block as PageCustomerServiceBanner}
		{#if csb.showBanner}
			<CustomerServiceBanner
				title={csb.title}
				showMessageInput={csb.showMessageInput}
				contactForm={csb.showContactForm}
				customPhone={csb.customPhone}
				customEmail={csb.customEmail}
				customFeedback={csb.customFeedback}
			/>
		{/if}
	{:else if block._type === "gallery"}
		{@const gallery = block as PageGallery}
		{#if gallery.images.length === 1}
			{@const image = gallery.images[0]}
			<div class="mx-edge-x laptop:mx-auto max-w-prose my-8">
				<Picture
					pictureObject={(image as SanityPicture) || {}}
					alt={image?.alt || ""}
					sizes={`(min-width: ${br.desktop}) 85vw, (min-width: ${br.laptop}) 90vw, 100vw`}
					class="h-full w-full object-cover rounded-2xl"
				/>
			</div>
		{:else}
			<div
				class="my-8 mx-edge-x laptop:mx-auto flex flex-wrap gap-4 items-center laptop:justify-center"
			>
				{#each gallery.images as image, i (i)}
					<Picture
						pictureObject={(image as SanityPicture) || {}}
						sizes={`(min-width: ${br.wide}) 20vw, (min-width: ${br.desktop}) 30vw, (min-width: ${br.tablet}) 40vw, 100vw`}
						alt={image?.alt || ""}
						class="rounded-xl object-cover"
					/>
				{/each}
			</div>
		{/if}
	{/if}
{/each}

{#snippet Info(content: AvtaleInfo)}
	<div class="flex flex-col tablet:flex-row gap-4 py-6">
		<h2 class="basis-1/4 shrink-0 mb-0">{content.title}</h2>
		<div class="basis-3/4">
			<PortableText value={content.description?.filter(Boolean) ?? []} />
		</div>
	</div>
{/snippet}
